/**
 * Excel Export Utility
 * Handles Excel file downloads from backend API endpoints
 * Supports multiple export types: FMEA, Risk Matrix, Pareto, Components
 */

import { API_BASE_URL } from '../api/config'
import { showToast } from '../components/Notifications/Toast'
import { PROJECT_ID } from '../api/interlockApi'

export type ExcelExportType = 'fmea' | 'risk-matrix' | 'pareto' | 'components'

interface ExcelExportOptions {
  type: ExcelExportType
  // Bug fix: the excel export endpoints used to be called with no project
  // scope, which made the backend return data across every project
  // (confirmed cross-project data leak). Now required, matching the
  // ?projectId= the backend enforces — defaults to the app's single-project
  // v1 constant so existing callers don't need to change.
  projectId?: string
  filename?: string
  onProgress?: (progress: number) => void
  onSuccess?: () => void
  onError?: (error: Error) => void
}

/**
 * Export data to Excel file
 * Downloads Excel file from backend API endpoint
 *
 * @param options - Export configuration
 * @returns Promise that resolves when download completes
 *
 * @example
 * ```typescript
 * // Export complete FMEA
 * await exportToExcel({ type: 'fmea' })
 *
 * // Export risk matrix with custom filename
 * await exportToExcel({
 *   type: 'risk-matrix',
 *   filename: 'Custom_Risk_Matrix.xlsx'
 * })
 *
 * // Export with progress tracking
 * await exportToExcel({
 *   type: 'pareto',
 *   onProgress: (p) => console.log(`${p}%`),
 *   onSuccess: () => console.log('Done!'),
 *   onError: (e) => console.error(e)
 * })
 * ```
 */
export async function exportToExcel(options: ExcelExportOptions): Promise<void> {
  const { type, projectId = PROJECT_ID, filename, onProgress, onSuccess, onError } = options

  // Map export type to API endpoint, scoped to the requested project
  const endpoint = `/exports/excel/${type}?projectId=${encodeURIComponent(projectId)}`
  const url = `${API_BASE_URL}${endpoint}`

  // Show loading toast
  const loadingToastId = showToast.loading('Generating Excel export...')

  try {
    // Report initial progress
    onProgress?.(10)

    // Fetch Excel file from backend
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    })

    onProgress?.(50)

    if (!response.ok) {
      const errorText = await response.text()
      let errorMessage = 'Failed to export Excel file'

      try {
        const errorData = JSON.parse(errorText)
        errorMessage = errorData.message || errorMessage
      } catch {
        // Response wasn't JSON, use default message
      }

      throw new Error(errorMessage)
    }

    // Get filename from Content-Disposition header or use provided/default
    const contentDisposition = response.headers.get('Content-Disposition')
    const filenameMatch = contentDisposition?.match(/filename="(.+)"/)
    const downloadFilename = filename || filenameMatch?.[1] || `FMEA_Export_${type}_${new Date().toISOString().split('T')[0]}.xlsx`

    onProgress?.(75)

    // Create blob from response
    const blob = await response.blob()

    onProgress?.(90)

    // Trigger browser download
    const downloadUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = downloadUrl
    a.download = downloadFilename
    document.body.appendChild(a)
    a.click()

    // Cleanup
    window.URL.revokeObjectURL(downloadUrl)
    document.body.removeChild(a)

    onProgress?.(100)

    // Dismiss loading toast and show success
    showToast.dismiss(loadingToastId)
    showToast.success(`Excel file downloaded: ${downloadFilename}`)

    onSuccess?.()
  } catch (error) {
    // Dismiss loading toast and show error
    showToast.dismiss(loadingToastId)

    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
    showToast.error(`Export failed: ${errorMessage}`)

    onError?.(error instanceof Error ? error : new Error(errorMessage))

    throw error
  }
}

/**
 * Export complete FMEA data to Excel
 * Convenience wrapper for exporting all FMEA data
 */
export async function exportFMEAToExcel(filename?: string, projectId?: string): Promise<void> {
  return exportToExcel({ type: 'fmea', filename, projectId })
}

/**
 * Export risk matrix to Excel
 * Convenience wrapper for exporting risk matrix with heat map
 */
export async function exportRiskMatrixToExcel(filename?: string, projectId?: string): Promise<void> {
  return exportToExcel({ type: 'risk-matrix', filename, projectId })
}

/**
 * Export Pareto analysis to Excel
 * Convenience wrapper for exporting Pareto analysis
 */
export async function exportParetoToExcel(filename?: string): Promise<void> {
  return exportToExcel({ type: 'pareto', filename })
}

/**
 * Export component hierarchy to Excel
 * Convenience wrapper for exporting component tree
 */
export async function exportComponentsToExcel(filename?: string): Promise<void> {
  return exportToExcel({ type: 'components', filename })
}
